import { AfterViewInit, Component, inject, signal, TemplateRef, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { TranslatePipe, TranslateService, _ } from '@ngx-translate/core';
import { catchError, combineLatest, filter, of, switchMap, take } from 'rxjs';

import { BaseComponent } from 'src/app/components/_base/base.component';
import { CONSTANTS } from 'src/app/providers/constants';
import { ENVIRONMENT } from 'src/app/providers/environment';
import { AuthService } from 'src/app/services/auth/auth.service';
import { LayoutService } from 'src/app/services/layout/layout.service';
import { helpCentreUrl } from 'src/app/shared/help-centre';
import { Roles } from 'src/app/types/identity';

@Component({
  selector: 'app-theme-extension-banner',
  standalone: true,
  templateUrl: './theme-extension-banner.component.html',
  imports: [TranslatePipe, MatButtonModule],
  styles: `
    button[mat-flat-button],
    button[mat-stroked-button] {
      --mdc-filled-button-container-height: 24px;
      --mdc-outlined-button-container-height: 24px;
      --mdc-filled-button-label-text-size: 12px;
      --mdc-outlined-button-label-text-size: 12px;
      line-height: 1;

      @media (min-width: 1280px) {
        --mdc-filled-button-container-height: 28px;
        --mdc-outlined-button-container-height: 28px;
      }
    }
  `,
})
export class ThemeExtensionBannerComponent extends BaseComponent implements AfterViewInit {
  private readonly authService = inject(AuthService);
  private readonly layoutService = inject(LayoutService);
  private readonly translate = inject(TranslateService);

  readonly constants = inject(CONSTANTS);
  private readonly env = inject(ENVIRONMENT);

  /** The help centre until it has an article on the app embed (GOO-475). */
  readonly guideUrl = helpCentreUrl(this.env.frontDomain);

  readonly deepLinkUrl = signal('');
  readonly embedDeepLink = signal('');

  readonly guideRef = viewChild.required<TemplateRef<unknown>>('guide');

  ngAfterViewInit() {
    // The storefront button only applies to clients, so never call the client-only
    // install-status endpoints (or show the banner) for admins / unauthenticated users.
    // The size guide button can be added either via App embed (recommended) or via an
    // App block. Show the reminder banner only when neither method is installed, and
    // surface both deep links so the user can pick whichever they prefer.
    this.sub$ = this.authService.user$
      .pipe(
        filter((user) => user?.role === Roles.Client),
        take(1),
        switchMap(() =>
          combineLatest([
            this.authService.fetchAppBlockState().pipe(catchError(() => of({ result: false, deepLink: '' }))),
            this.authService.fetchAppEmbedState().pipe(catchError(() => of({ result: false, deepLink: '' }))),
          ]),
        ),
      )
      .subscribe(([block, embed]) => {
        this.deepLinkUrl.set(block.deepLink);
        this.embedDeepLink.set(embed.deepLink);

        if (!block.result && !embed.result)
          this.layoutService.banner = {
            id: this.constants.BANNER_SHOPIFY_GUIDE as string,
            templateRef: this.guideRef(),
            disableClose: true,
            title: this.translate.instant(_('banner.add-the-goodchart-size-guide-button-to-your-storefront')),
          };
      });
  }

  onEnableEmbed() {
    if (this.embedDeepLink()) window.open(this.embedDeepLink(), '_blank');
  }

  onAddBlock() {
    if (this.deepLinkUrl()) window.open(this.deepLinkUrl(), '_blank');
  }
}
